import * as React from 'react';
import _ from 'lodash';
import type { FilterItem, FilterItemAll, LogicalAndFilterItem, LogicalOrFilterItem } from '../../../../types/Filter';
import Dropdown from '../../Dropdown/Dropdown';
import type { AvailableGroupingFilter, OnFilterChangeHandler, RenderFilterItemHandler } from '../types';
import { AddFilterButton } from '../AddFilter/AddFilter';
import { isTruthy } from '../../../../modules/code-utils';
import { mapFilterRootItem } from '../utils';
import DeleteFilterButton from './DeleteFilterButton';
import cn from '~modules/cn';
import Styles from './filter-views.module.scss';

type Props = {
    filterItem: LogicalAndFilterItem | LogicalOrFilterItem;
    deletable?: boolean;
    onChange: OnFilterChangeHandler;
    indent: number;
    renderFilterItem: RenderFilterItemHandler;
    allAvailableFilters: FilterItem[];
    locale: string | null;
    availableGroupingFilter: AvailableGroupingFilter;
};

const AndOrFilterView = (props: Props) => {
    const { filterItem } = props;

    const items = React.useMemo(
        () =>
            [props.availableGroupingFilter.and && { name: 'And', value: 'and' }, props.availableGroupingFilter.or && { name: 'Or', value: 'or' }].filter(
                isTruthy
            ),
        [props.availableGroupingFilter]
    );

    const nextFilters = React.useMemo(() => {
        if (props.indent) {
            // for nested - not allow groups (so only first level is allowed)
            return {};
        }
        const nestedKey = filterItem.type === 'and' ? 'or' : 'and';
        if (!props.availableGroupingFilter[nestedKey]) {
            // e.g. OR is disabled -> not allow it
            return {};
        }
        return {
            [nestedKey]: true
        };
    }, [filterItem.type, props.availableGroupingFilter, props.indent]);

    const availableFilters = React.useMemo(() => {
        const filters: FilterItemAll[] = [...filterItem.availableFilters];
        if (filterItem.filters.length > 0) {
            Object.keys(nextFilters).forEach((type) => {
                filters.push({
                    type: type as 'and' | 'or',
                    filters: [],
                    availableFilters: props.allAvailableFilters
                });
            });
        }
        return filters;
    }, [filterItem.availableFilters, filterItem.filters.length, nextFilters, props.allAvailableFilters]);

    return (
        <div className={Styles['logic-filter-container']}>
            <div
                className={cn(Styles['filters-container'], {
                    [Styles['filters-container-has-filters']]: filterItem.filters.length > 0,
                    [Styles['filters-container-nested']]: props.indent > 0,
                    [Styles['filters-container-nested-even']]: props.indent % 2 === 0,
                    [Styles['filters-container-nested-odd']]: props.indent % 2 !== 0
                })}
            >
                <div className={Styles['filters-inner-container']}>
                    {!filterItem.filters.length && <div className={Styles['logical-filter-name']}>Where</div>}
                    {filterItem.filters.map((childFilterItem, index) => {
                        const logicalFilterNameClass = cn(Styles['logical-filter-name'], {
                            [Styles['logical-filter-name-nested']]: childFilterItem.type === 'and' || childFilterItem.type === 'or'
                        });
                        return (
                            <React.Fragment key={index}>
                                {index === 0 && <div className={logicalFilterNameClass}>Where</div>}
                                {index === 1 &&
                                    (items.length > 1 ? (
                                        <Dropdown
                                            options={items}
                                            value={filterItem.type}
                                            className={Styles.control}
                                            menuClassName={Styles['filter-view-dropdown-tooltip']}
                                            onChange={(newValue) => {
                                                if (newValue !== filterItem.type) {
                                                    const allowedChildGroup = newValue === 'and' ? 'or' : 'and';
                                                    const newFilterItem = mapFilterRootItem(filterItem, (childFilter) => {
                                                        if (childFilter === filterItem) {
                                                            return filterItem;
                                                        }
                                                        if (childFilter.type === 'and' || childFilter.type === 'or') {
                                                            return {
                                                                ...childFilter,
                                                                type: allowedChildGroup
                                                            };
                                                        }
                                                        return childFilter;
                                                    });
                                                    props.onChange({
                                                        ...newFilterItem!,
                                                        type: newValue as 'and' | 'or'
                                                    });
                                                }
                                            }}
                                        />
                                    ) : (
                                        <div className={logicalFilterNameClass}>{_.upperFirst(filterItem.type)}</div>
                                    ))}
                                {index > 1 && <div className={logicalFilterNameClass}>{_.upperFirst(filterItem.type)}</div>}
                                {props.renderFilterItem({
                                    index,
                                    indent: props.indent + 1,
                                    item: childFilterItem,
                                    availableFilters: filterItem.availableFilters,
                                    selectedLocale: props.locale,
                                    availableGroupingFilter: nextFilters,
                                    allAvailableFilters: props.allAvailableFilters,
                                    onChange: (updatedFilterItem) => {
                                        const filters = [...filterItem.filters];
                                        if (updatedFilterItem) {
                                            filters[index] = updatedFilterItem;
                                        } else {
                                            filters.splice(index, 1);
                                        }
                                        props.onChange?.({
                                            ...filterItem,
                                            filters
                                        });
                                    }
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>
                <AddFilterButton
                    className={Styles['add-filter-button']}
                    buttonType="primary"
                    availableFilters={availableFilters}
                    onAdd={(newFilterItem) => {
                        const filters = [...filterItem.filters, newFilterItem];
                        props.onChange({
                            ...filterItem,
                            filters
                        });
                    }}
                />
            </div>
            {props.deletable && <DeleteFilterButton {...props} className={Styles['logical-filter-delete-button']} />}
        </div>
    );
};

export default AndOrFilterView;
